import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { Subscribable } from '../../../../models/base/subscribable';
import { map, take } from 'rxjs/operators';
import { TypeService } from '../../../../services/type/type-service';
import { BehaviorSubject, combineLatest, forkJoin, Observable } from 'rxjs';
import { Country } from '../../../../models/types/dto/country';
import { Gender } from '../../../../models/types/dto/gender';
import { Pronoun } from '../../../../models/users/dto/pronoun';
import { MemberUser } from '../../../../models/users/dto/member-user';
import { UserDomainModel } from '../../../../domainModels/user-domain-model';
import {
  buildMemberPersonalInformationOnboardingFormGroup,
  MemberPersonalInformationOnboardingFormGroup
} from './member-personal-information-onboarding-form-group';
import { CustomError } from '../../../../models/shared/custom-error';
import { MembersDomainModel } from '../../../../domainModels/members-domain-model';
import { MemberPersonalInformationOnboardingRequest } from '../../../../models/users/requests/member-personal-information-onboarding-request';
import { ToastService } from '../../../../services/toast-service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UserAPI } from '../../../../api/user-api';
import { MemberSIN } from '../../../../models/users/dto/member-sin';

@Component({
  selector: 'app-member-personal-information-onboarding-modal',
  templateUrl: './member-personal-information-onboarding-modal.component.html',
  styleUrls: ['./member-personal-information-onboarding-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MemberPersonalInformationOnboardingModalComponent extends Subscribable implements OnInit {
  private typeService = inject(TypeService);
  private userDomainModel = inject(UserDomainModel);
  private membersDomainModel = inject(MembersDomainModel);
  private toastService = inject(ToastService);
  private activeModal = inject(NgbActiveModal);
  private userApi = inject(UserAPI);

  constructor() {
    super();
  }

  public user$ = this.userDomainModel.user$.pipe(map(user => user as MemberUser));

  private _countries = new BehaviorSubject<Country[]>([]);
  public countries$ = this._countries as Observable<Country[]>;

  private _memberSin = new BehaviorSubject<MemberSIN | null>(null);
  public memberSin$ = this._memberSin.exists();

  private _genders = new BehaviorSubject<Gender[]>([]);
  public genders$ = this._genders as Observable<Gender[]>;

  private _pronouns = new BehaviorSubject<Pronoun[]>([]);
  public pronouns$ = this._pronouns as Observable<Pronoun[]>;

  private _isLoading = new BehaviorSubject<boolean>(false);
  public isLoading$ = this._isLoading as Observable<boolean>;

  private _formGroup = new BehaviorSubject<MemberPersonalInformationOnboardingFormGroup | null>(null);
  public formGroup$ = this._formGroup.exists();

  private listenToUserInfo = combineLatest([this.user$, this.memberSin$]).subscribeWhileAlive({
    owner: this,
    next: ([user, sin]) => {
      this._formGroup.next(this.buildForm(user, sin));
    }
  });

  public formSubmitted = false;

  private buildForm(u: MemberUser, sin: MemberSIN): MemberPersonalInformationOnboardingFormGroup {
    return buildMemberPersonalInformationOnboardingFormGroup(u, sin);
  }

  public ngOnInit() {
    this._isLoading.next(true);

    forkJoin({
      /* eslint-disable rxjs/finnish  */
      countries: this.typeService.countries$.pipe(take(1)),
      genders: this.typeService.genders$.pipe(take(1)),
      pronouns: this.typeService.pronouns$.pipe(take(1)),
      sin: this.userApi.getMemberSIN()
      /* eslint-enable rxjs/finnish  */
    }).subscribe(({ countries, genders, pronouns, sin }) => {
      this._countries.next(countries);
      this._genders.next(genders);
      this._pronouns.next(pronouns);
      this._memberSin.next(sin);
      this._isLoading.next(false);
    });
  }

  public submitForm(): void {
    this.formGroup$.once(fg => {
      this.formSubmitted = true;
      if (fg.invalid) {
        return;
      }
      const req = new MemberPersonalInformationOnboardingRequest(fg);
      this.updateMemberOnboardingInformation(req);
    });
  }

  private updateMemberOnboardingInformation(req: MemberPersonalInformationOnboardingRequest): void {
    this._isLoading.next(true);
    this.membersDomainModel.updateMemberOnboardingInformation(req).subscribe({
      next: user => {
        this._isLoading.next(false);
        this.activeModal.close(user);
      },
      error: (error: CustomError) => {
        this._isLoading.next(false);
        this.toastService.publishError(error);
      }
    });
  }
}
